import axios from 'axios';

export const add404ErrorInterceptor = (axiosInstance) => {
  axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => {
      const url = error.config?.url || '';
      
      // Handle 404 errors silently for specific endpoints
      if (error.response?.status === 404) {
        const silent404Endpoints = [
          '/api/punching-times/current',
          '/api/punching-times/record-login',
          '/api/bank-details',
          '/api/quotations',
          '/api/quotations-pdf'
        ];
        
        if (silent404Endpoints.some(endpoint => url.includes(endpoint))) {
          // Return a resolved promise to prevent the error from propagating
          return Promise.resolve({
            data: null,
            status: 404,
            statusText: 'Not Found',
            headers: {},
            config: error.config
          });
        }
      }
      
      // Handle 400 errors silently for punching-times endpoints
      if (error.response?.status === 400) {
        const silent400Endpoints = [
          '/api/punching-times/record-login',
          '/api/punching-times/current'
        ];
        
        if (silent400Endpoints.some(endpoint => url.includes(endpoint))) {
          console.log(`🔇 Silencing 400 error for ${url} (punching times feature not available)`);
          // Return a resolved promise to prevent the error from propagating
          return Promise.resolve({
            data: null,
            status: 400,
            statusText: 'Bad Request',
            headers: {},
            config: error.config
          });
        }
      }
      
      // For other errors, let them propagate normally
      return Promise.reject(error);
    }
  );
};

// Add global axios interceptor to handle 404 errors gracefully
add404ErrorInterceptor(axios);

// Add request interceptor to ensure fresh tokens
axios.interceptors.request.use(
  async (config) => {
    try {
      const { auth } = await import('../firebase');
      const user = auth.currentUser;
      
      if (user && config.url && config.url.includes('/api/')) {
        const token = await user.getIdToken(true);
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      console.error('❌ Failed to get fresh token:', error);
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

// Add authentication interceptor to handle token refresh
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // Handle 401 errors (token expired)
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        // Get fresh token from Firebase Auth
        const { auth } = await import('../firebase');
        const user = auth.currentUser;
        
        if (user) {
          console.log('🔄 Token expired, refreshing...');
          const newToken = await user.getIdToken(true);
          
          // Update the authorization header
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          
          // Retry the original request
          return axios(originalRequest);
        }
      } catch (refreshError) {
        console.error('❌ Token refresh failed:', refreshError);
        // Redirect to login or handle auth failure
        localStorage.removeItem('token');
        window.location.href = '/login';
      }
    }
    
    return Promise.reject(error);
  }
);


const PRODUCTION_API_URL = process.env.REACT_APP_API_URL || 'https://wyenfos-api-615596991593.asia-south1.run.app';


export const getApiUrl = () => {
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'http://localhost:5000';
  }
  // Production API URL
  return PRODUCTION_API_URL;
};

export const getCORSFreeApiUrl = () => {
  const isLocalhost = window.location.hostname === 'localhost' || 
                     window.location.hostname === '127.0.0.1';
  
  if (isLocalhost) {
    return 'http://localhost:5000';
  }
  
  // Use production Google Cloud Run API
  return PRODUCTION_API_URL;
};


const getWebSocketUrl = () => {
  // WebSocket not supported in Firebase Functions, use polling instead
  return null;
};

export const API_CONFIG = {
  BASE_URL: getCORSFreeApiUrl(),
  WEBSOCKET_URL: getWebSocketUrl(),
  TIMEOUT: 30000,
  HEADERS: {
    'Content-Type': 'application/json',
  },
  USE_FIREBASE_ONLY: getCORSFreeApiUrl() === null, // Use Firebase when API is not available
};

export const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    ...API_CONFIG.HEADERS,
    Authorization: token ? `Bearer ${token}` : '',
  };
};

